/**
 * 验证码输入组件
 *
 * 功能:
 * - 6 位数字输入框
 * - 自动聚焦第一个输入框
 * - 输入后自动跳转到下一个
 * - 支持退格键删除
 * - 支持粘贴完整验证码
 * - 样式：方框样式，聚焦时高亮
 */

import React, { useRef, useState, useEffect, useCallback } from 'react';

interface VerificationCodeInputProps {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  disabled?: boolean;
  autoFocus?: boolean;
  className?: string;
}

export const VerificationCodeInput: React.FC<VerificationCodeInputProps> = ({
  value,
  onChange,
  length = 6,
  disabled = false,
  autoFocus = true,
  className = '',
}) => {
  const [focusedIndex, setFocusedIndex] = useState<number>(0);
  const inputRefs = useRef<HTMLInputElement[]>([]);

  const values = value.split('').concat(Array(length).fill('')).slice(0, length);

  const focusInput = useCallback((index: number) => {
    if (index >= 0 && index < length && inputRefs.current[index]) {
      inputRefs.current[index].focus();
      setFocusedIndex(index);
    }
  }, [length]);

  useEffect(() => {
    if (autoFocus && !disabled) {
      focusInput(0);
    }
  }, [autoFocus, disabled, focusInput]);

  const handleChange = useCallback(
    (index: number, inputValue: string) => {
      const digit = inputValue.replace(/\D/g, '').slice(0, 1);
      if (!digit && inputValue !== '') return;

      const newValues = [...values];
      newValues[index] = digit;
      const newValue = newValues.join('');
      onChange(newValue);

      if (digit && index < length - 1) {
        focusInput(index + 1);
      }
    },
    [values, length, onChange, focusInput]
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace') {
        if (!values[index] && index > 0) {
          focusInput(index - 1);
        } else {
          const newValues = [...values];
          newValues[index] = '';
          onChange(newValues.join(''));
        }
      } else if (e.key === 'ArrowLeft' && index > 0) {
        focusInput(index - 1);
      } else if (e.key === 'ArrowRight' && index < length - 1) {
        focusInput(index + 1);
      } else if (e.key === 'Enter' && value.length === length) {
        const form = e.currentTarget.form;
        if (form) {
          form.dispatchEvent(
            new Event('submit', { bubbles: true, cancelable: true })
          );
        }
      }
    },
    [values, length, value, onChange, focusInput]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
      onChange(pastedData);
      focusInput(Math.min(pastedData.length, length - 1));
    },
    [length, onChange, focusInput]
  );

  const handleFocus = useCallback((index: number) => {
    setFocusedIndex(index);
    inputRefs.current[index]?.select();
  }, []);

  return (
    <div className={`flex gap-2 ${className}`}>
      {values.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            if (el) inputRefs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={() => handleFocus(index)}
          disabled={disabled}
          className={`w-12 h-14 text-center text-2xl font-semibold border-2 rounded-lg
            transition-all duration-200 focus:outline-none
            ${
              focusedIndex === index
                ? 'border-paper-500 ring-2 ring-paper-500/20 bg-paper-50'
                : 'border-gray-200'
            }
            ${
              disabled
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white hover:border-gray-300'
            }
          `}
        />
      ))}
    </div>
  );
};

export default VerificationCodeInput;